/// <reference lib="dom" />
import { WebPlugin } from '@capacitor/core';
import type { MediaSessionPlugin } from './definitions';

type MediaSessionAction = 'previoustrack' | 'seekbackward' | 'play' | 'pause' | 'seekforward' | 'nexttrack' | 'seekto' | 'stop';

interface MediaMetadataInit {
  title?: string;
  artist?: string;
  album?: string;
  artwork?: Array<{ src: string; sizes?: string; type?: string }>;
}

declare global {
  interface Navigator {
    readonly mediaSession?: {
      metadata: MediaMetadata | null;
      playbackState: 'none' | 'paused' | 'playing';
      setPositionState(state: { duration?: number; playbackRate?: number; position?: number }): void;
      setActionHandler(action: MediaSessionAction, handler: () => void): void;
    };
  }

  class MediaMetadata {
    constructor(init: MediaMetadataInit);
  }
}

export class MediaSessionWeb extends WebPlugin implements MediaSessionPlugin {
  async setMetadata(options: { title?: string; artist?: string; album?: string; artwork?: Array<{ src: string }> }): Promise<void> {
    if ('mediaSession' in navigator && navigator.mediaSession) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: options.title,
        artist: options.artist,
        album: options.album,
        artwork: options.artwork,
      });
    }
  }

  async setPlaybackState(options: { playbackState: 'none' | 'paused' | 'playing' }): Promise<void> {
    if ('mediaSession' in navigator && navigator.mediaSession) {
      navigator.mediaSession.playbackState = options.playbackState;
    }
  }

  async setPositionState(options: { duration?: number; position?: number; playbackRate?: number }): Promise<void> {
    if ('mediaSession' in navigator && navigator.mediaSession && 'setPositionState' in navigator.mediaSession) {
      navigator.mediaSession.setPositionState({
        duration: options.duration,
        position: options.position,
        playbackRate: options.playbackRate,
      });
    }
  }

  async setActionHandler(options: { action: MediaSessionAction }): Promise<void> {
    if ('mediaSession' in navigator && navigator.mediaSession && 'setActionHandler' in navigator.mediaSession) {
      navigator.mediaSession.setActionHandler(options.action, () => {
        this.notifyListeners(options.action, {});
      });
    }
  }
}
